import React, { useState, useEffect, useContext } from 'react';
import { 
  TextField, Button, FormControl, InputLabel, Select, MenuItem, 
  FormHelperText, Grid, Switch, FormControlLabel, CircularProgress, Alert
} from '@mui/material';
import { useMutation, useQuery, gql } from '@apollo/client';
import AuthContext from '../contexts/AuthContext';
import SearchableSelect from './SearchableSelect';
import { StyledFormBox } from './StyledComponents';
import PhoneNumberField from './PhoneNumberField';
import { isValidPhoneNumber } from 'react-phone-number-input';

// GraphQL queries et mutations
const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
    }
  }
`;

const GET_FUNCTIONS = gql`
  query GetFunctions {
    functions {
      id
      name
      department {
        id
        name
      }
    }
  }
`;

const CREATE_FUNCTION = gql`
  mutation CreateFunction($name: String!, $departmentId: ID) {
    createFunction(name: $name, departmentId: $departmentId) {
      success
      message
      function {
        id
        name
        department {
          id
          name
        }
      }
    }
  }
`;

// Valeur spéciale pour l'option "Autre" du select de fonction
const OTHER_FUNCTION_VALUE = '__OTHER__';

const CREATE_USER = gql`
  mutation CreateUser(
    $email: String!
    $username: String!
    $password: String!
    $firstName: String!
    $lastName: String!
    $role: String!
    $departmentId: ID
    $functionId: ID
    $phoneNumber: BigInt
    $otpEnabled: Boolean
  ) {
    createUser(
      email: $email
      username: $username
      password: $password
      firstName: $firstName
      lastName: $lastName
      role: $role
      departmentId: $departmentId
      functionId: $functionId
      phoneNumber: $phoneNumber
      otpEnabled: $otpEnabled
    ) {
      success
      message
      user {
        id
        email
        username
        firstName
        lastName
        role
        department {
          id
          name
        }
        function {
          id
          name
        }
        phoneNumber
        otpEnabled
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser(
    $id: ID!
    $email: String
    $username: String
    $password: String
    $firstName: String
    $lastName: String
    $role: String
    $departmentId: ID
    $functionId: ID
    $phoneNumber: BigInt
    $otpEnabled: Boolean
  ) {
    updateUser(
      id: $id
      email: $email
      username: $username
      password: $password
      firstName: $firstName
      lastName: $lastName
      role: $role
      departmentId: $departmentId
      functionId: $functionId
      phoneNumber: $phoneNumber
      otpEnabled: $otpEnabled
    ) {
      success
      message
      user {
        id
        email
        username
        firstName
        lastName
        role
        department {
          id
          name
        }
        function {
          id
          name
        }
        phoneNumber
        otpEnabled
      }
    }
  }
`;

function UserForm({ onSuccess, initialData, isEdit = false, defaultRole = null }) {
  const { user } = useContext(AuthContext);
  
  // Département par défaut : celui de l'utilisateur édité, sinon celui du gestionnaire connecté
  // (uniquement en création d'un membre du personnel, pas pour les visiteurs)
  const defaultDepartmentId = initialData
    ? (initialData.department?.id || '')
    : (user?.role === 'MANAGER' && defaultRole !== 'VISITOR' ? (user?.department?.id || '') : '');

  // État du formulaire
  const [formValues, setFormValues] = useState({
    id: initialData?.id || '',
    email: initialData?.email || '',
    username: initialData?.username || '',
    password: '',
    confirmPassword: '',
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    role: initialData?.role || defaultRole || 'HOST',
    departmentId: defaultDepartmentId,
    functionId: initialData?.function?.id || '',
    phoneNumber: initialData?.phoneNumber ? `+${initialData.phoneNumber}` : '',
    otpEnabled: initialData?.otpEnabled || false
  });

  // Nom de la nouvelle fonction (option "Autre")
  const [newFunctionName, setNewFunctionName] = useState('');
  
  // État des erreurs
  const [formErrors, setFormErrors] = useState({});
  
  // État des champs touchés
  const [touchedFields, setTouchedFields] = useState({});
  
  // État des messages
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Récupérer la liste des départements
  const { data: departmentsData, loading: departmentsLoading } = useQuery(GET_DEPARTMENTS);
  
  // Récupérer la liste des fonctions
  const { data: functionsData, loading: functionsLoading } = useQuery(GET_FUNCTIONS);

  // Liste des départements, en incluant celui de l'utilisateur édité s'il est absent
  // (ex: les listes sont restreintes selon le rôle de l'utilisateur connecté)
  const departmentOptions = React.useMemo(() => {
    const departments = departmentsData?.departments || [];
    const currentDepartment = initialData?.department;
    if (currentDepartment && !departments.some((dept) => String(dept.id) === String(currentDepartment.id))) {
      return [currentDepartment, ...departments];
    }
    return departments;
  }, [departmentsData, initialData]);

  // Fonctions filtrées par département sélectionné
  const filteredFunctions = React.useMemo(() => {
    const functions = functionsData?.functions || [];
    let filtered = functions;
    if (formValues.departmentId) {
      filtered = functions.filter(
        (func) => !func?.department || String(func.department.id) === String(formValues.departmentId)
      );
    }
    // Toujours inclure la fonction actuelle de l'utilisateur édité pour le préchargement
    const currentFunction = initialData?.function;
    if (currentFunction && !filtered.some((func) => String(func.id) === String(currentFunction.id))) {
      filtered = [currentFunction, ...filtered];
    }
    return filtered;
  }, [functionsData, formValues.departmentId, initialData]);

  // Options du select de fonction avec l'option "Autre" pour créer une nouvelle fonction
  const functionOptions = React.useMemo(() => ([
    ...filteredFunctions,
    { id: OTHER_FUNCTION_VALUE, name: 'Autre… (créer une nouvelle fonction)' }
  ]), [filteredFunctions]);
  
  // Mutation pour créer une nouvelle fonction (option "Autre")
  const [createFunction, { loading: createFunctionLoading }] = useMutation(CREATE_FUNCTION, {
    refetchQueries: ['GetFunctions'],
  });

  // Mutation pour créer un utilisateur
  const [createUser, { loading: createLoading }] = useMutation(CREATE_USER, {
    onCompleted: (data) => {
      if (data.createUser.success) {
        setSuccessMessage('Utilisateur créé avec succès');
        setErrorMessage('');
        
        // Réinitialiser le formulaire
        resetForm();
        
        if (onSuccess) {
          onSuccess(data.createUser.user);
        }
      } else {
        setErrorMessage(data.createUser.message);
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      setErrorMessage(`Erreur: ${error.message}`);
      setSuccessMessage('');
    }
  });
  
  // Mutation pour mettre à jour un utilisateur
  const [updateUser, { loading: updateLoading }] = useMutation(UPDATE_USER, {
    onCompleted: (data) => {
      if (data.updateUser.success) {
        setSuccessMessage('Utilisateur mis à jour avec succès');
        setErrorMessage('');
        
        if (onSuccess) {
          onSuccess(data.updateUser.user);
        }
      } else {
        setErrorMessage(data.updateUser.message);
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      setErrorMessage(`Erreur: ${error.message}`);
      setSuccessMessage('');
    }
  });
  
  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormValues({
      id: '',
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      role: defaultRole || 'HOST',
      departmentId: (user?.role === 'MANAGER' && defaultRole !== 'VISITOR') ? (user?.department?.id || '') : '',
      functionId: '',
    phoneNumber: '',
      otpEnabled: false
    });
    setNewFunctionName('');
    setFormErrors({});
    setTouchedFields({});
  };
  
  // Mettre à jour les valeurs initiales lorsque initialData change
  useEffect(() => {
    if (initialData) {
      setFormValues({
        id: initialData.id || '',
        email: initialData.email || '',
        username: initialData.username || '',
        password: '',
        confirmPassword: '',
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        role: initialData.role || defaultRole || 'HOST',
        departmentId: initialData.department?.id || '',
        functionId: initialData.function?.id || '',
        phoneNumber: initialData.phoneNumber ? `+${initialData.phoneNumber}` : '',
        otpEnabled: initialData.otpEnabled || false
      });
      setNewFunctionName('');
      // Réinitialiser les erreurs et les champs touchés
      setFormErrors({});
      setTouchedFields({});
    }
  }, [initialData, defaultRole]);

  // Si la fonction ne correspond plus au département sélectionné, la réinitialiser.
  // Attendre que la liste des fonctions soit chargée pour ne pas effacer la valeur préchargée.
  useEffect(() => {
    if (!formValues.functionId || functionsLoading || !functionsData) return;
    if (formValues.functionId === OTHER_FUNCTION_VALUE) return;
    const stillValid = filteredFunctions.some((func) => String(func.id) === String(formValues.functionId));
    if (!stillValid) {
      setFormValues((prev) => ({ ...prev, functionId: '' }));
    }
  }, [formValues.functionId, filteredFunctions, functionsLoading, functionsData]);
  
  // Gérer les changements de champs
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Valider le champ
    validateField(name, type === 'checkbox' ? checked : value);
  };
  
  // Gérer la perte de focus
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields({
      ...touchedFields,
      [name]: true
    });
    validateField(name, formValues[name]);
  };
  
  // Valider un champ
  const validateField = (name, value) => {
    let error = null;
    
    switch (name) {
      case 'email':
        if (!value) {
          error = 'Email requis';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = 'Email invalide';
        }
        break;
      case 'username':
        if (!value) {
          error = 'Nom d\'utilisateur requis';
        } else if (value.length < 3) {
          error = 'Le nom d\'utilisateur doit contenir au moins 3 caractères';
        }
        break;
      case 'password':
        if (!isEdit && !value) {
          error = 'Mot de passe requis';
        } else if (value && value.length < 8) {
          error = 'Le mot de passe doit contenir au moins 8 caractères';
        }
        break;
      case 'confirmPassword':
        if (!isEdit && !value && formValues.password) {
          error = 'Confirmation du mot de passe requise';
        } else if (value && value !== formValues.password) {
          error = 'Les mots de passe doivent correspondre';
        }
        break;
      case 'firstName':
        if (!value) {
          error = 'Prénom requis';
        }
        break;
      case 'lastName':
        if (!value) {
          error = 'Nom requis';
        }
        break;
      case 'role':
        if (!value) {
          error = 'Rôle requis';
        }
        break;
      case 'departmentId':
        if (formValues.role !== 'VISITOR' && !value) {
          error = 'Département requis pour ce rôle';
        }
        break;
      case 'functionId':
        if (formValues.role !== 'VISITOR' && !value) {
          error = 'Fonction requise pour ce rôle';
        }
        break;
      case 'phoneNumber':
        if (value && !isValidPhoneNumber(value)) {
          error = 'Numéro de téléphone invalide';
        }
        break;
      default:
        break;
    }
    
    setFormErrors({
      ...formErrors,
      [name]: error
    });
    
    return error;
  };
  
  // Valider le formulaire
  const validateForm = () => {
    const errors = {};
    let isValid = true;
    
    // Valider tous les champs
    Object.keys(formValues).forEach(name => {
      // Ne pas valider le mot de passe et la confirmation en mode édition si vides
      if (isEdit && (name === 'password' || name === 'confirmPassword') && !formValues.password) {
        return;
      }
      
      const error = validateField(name, formValues[name]);
      if (error) {
        errors[name] = error;
        isValid = false;
      }
    });
    
    setFormErrors(errors);
    return isValid;
  };
  
  // Gérer la soumission du formulaire
  const toDigitsOrNull = (value) => {
    if (!value) return null;
    const digits = String(value).replace(/\D/g, '');
    if (!digits) return null;
    return parseInt(digits, 10);
  };

  // Résoudre l'ID de fonction : créer la nouvelle fonction si l'option "Autre" est sélectionnée
  const resolveFunctionId = async () => {
    if (formValues.functionId !== OTHER_FUNCTION_VALUE) {
      return formValues.functionId || null;
    }

    const { data } = await createFunction({
      variables: {
        name: newFunctionName.trim(),
        departmentId: formValues.departmentId || null
      }
    });

    if (!data?.createFunction?.success || !data.createFunction.function?.id) {
      throw new Error(data?.createFunction?.message || 'Erreur lors de la création de la fonction');
    }

    return data.createFunction.function.id;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marquer tous les champs comme touchés
    const allTouched = {};
    Object.keys(formValues).forEach(name => {
      // Ne pas marquer le mot de passe et la confirmation comme touchés en mode édition si vides
      if (isEdit && (name === 'password' || name === 'confirmPassword') && !formValues.password) {
        return;
      }
      allTouched[name] = true;
    });
    setTouchedFields(allTouched);
    
    // Vérifier le nom de la nouvelle fonction si l'option "Autre" est sélectionnée
    if (formValues.functionId === OTHER_FUNCTION_VALUE && !newFunctionName.trim()) {
      setErrorMessage('Veuillez saisir le nom de la nouvelle fonction');
      return;
    }
    
    // Valider le formulaire
    if (validateForm()) {
      console.log("Formulaire valide, soumission en cours...");
      
      let functionId;
      try {
        functionId = await resolveFunctionId();
      } catch (error) {
        setErrorMessage(error.message);
        setSuccessMessage('');
        return;
      }
      
      if (isEdit) {
        // Mettre à jour l'utilisateur
        const variables = {
          id: formValues.id,
          email: formValues.email,
          username: formValues.username,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          role: formValues.role,
          departmentId: formValues.departmentId || null,
          functionId: functionId,
          phoneNumber: toDigitsOrNull(formValues.phoneNumber),
          otpEnabled: formValues.otpEnabled
        };
        
        // Ajouter le mot de passe seulement s'il est fourni
        if (formValues.password) {
          variables.password = formValues.password;
        }
        
        updateUser({ variables })
          .catch(error => {
            console.error("Erreur lors de la mutation updateUser:", error);
          });
      } else {
        // Créer un nouvel utilisateur
        createUser({
          variables: {
            email: formValues.email,
            username: formValues.username,
            password: formValues.password,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            role: formValues.role,
            departmentId: formValues.departmentId || null,
            functionId: functionId,
            phoneNumber: toDigitsOrNull(formValues.phoneNumber),
            otpEnabled: formValues.otpEnabled
          }
        });
      }
    } else {
      console.log("Formulaire invalide, erreurs:", formErrors);
    }
  };
  
  const loading = createLoading || updateLoading || createFunctionLoading;
  
  return (
    <StyledFormBox>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email"
              value={formValues.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touchedFields.email && Boolean(formErrors.email)}
              helperText={touchedFields.email && formErrors.email}
              margin="normal"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="username"
              name="username"
              label="Nom d'utilisateur"
              value={formValues.username}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touchedFields.username && Boolean(formErrors.username)}
              helperText={touchedFields.username && formErrors.username}
              margin="normal"
              required
            />
          </Grid>
          {!isEdit && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Mot de passe"
                  type="password"
                  value={formValues.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touchedFields.password && Boolean(formErrors.password)}
                  helperText={touchedFields.password && formErrors.password}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirmer le mot de passe"
                  type="password"
                  value={formValues.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touchedFields.confirmPassword && Boolean(formErrors.confirmPassword)}
                  helperText={touchedFields.confirmPassword && formErrors.confirmPassword}
                  margin="normal"
                  required={!!formValues.password}
                  disabled={!formValues.password}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="firstName"
              name="firstName"
              label="Prénom"
              value={formValues.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touchedFields.firstName && Boolean(formErrors.firstName)}
              helperText={touchedFields.firstName && formErrors.firstName}
              margin="normal"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="lastName"
              name="lastName"
              label="Nom"
              value={formValues.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touchedFields.lastName && Boolean(formErrors.lastName)}
              helperText={touchedFields.lastName && formErrors.lastName}
              margin="normal"
              required
            />
          </Grid>
          {!defaultRole && (
          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              margin="normal"
              error={touchedFields.role && Boolean(formErrors.role)}
              required
            >
              <InputLabel id="role-label">Rôle</InputLabel>
              <Select
                labelId="role-label"
                id="role"
                name="role"
                value={formValues.role}
                label="Rôle"
                onChange={handleChange}
                onBlur={handleBlur}
              >
                {/* {user?.role === 'ADMIN' && (
                  <MenuItem value="ADMIN">Administrateur</MenuItem>
                )} */}
                {['ADMIN', 'MANAGER'].includes(user?.role) && (
                  <MenuItem value="MANAGER">Gestionnaire</MenuItem>
                )}
                {['ADMIN', 'MANAGER'].includes(user?.role) && (
                  <MenuItem value="ACCEUIL">Accueil</MenuItem>
                )}
                {['ADMIN', 'MANAGER', 'RH'].includes(user?.role) && (
                  <MenuItem value="RH">Ressources Humaines</MenuItem>
                )}
                {['ADMIN', 'MANAGER', 'RH'].includes(user?.role) && (
                  <MenuItem value="EMPLOYEE">Employé</MenuItem>
                )}
                <MenuItem value="HOST">Hôte</MenuItem>
              </Select>
              {touchedFields.role && formErrors.role && (
                <FormHelperText>{formErrors.role}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          )}
          {!defaultRole && (
          <Grid item xs={12} md={6}>
              <SearchableSelect
              margin="normal"
                label="Département"
                value={formValues.departmentId}
                onChange={(value) => {
                  setFormValues(prev => ({ ...prev, departmentId: value, functionId: '' }));
                  setTouchedFields(prev => ({ ...prev, departmentId: true }));
                }}
                options={departmentOptions}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                required={formValues.role !== 'VISITOR'}
                disabled={formValues.role === 'VISITOR' || departmentsLoading}
                error={touchedFields.departmentId && Boolean(formErrors.departmentId)}
                helperText={touchedFields.departmentId && formErrors.departmentId}
              />
          </Grid>
          )}
          {!defaultRole && (
          <Grid item xs={12} md={6}>
              <SearchableSelect
              margin="normal"
                label="Fonction"
                value={formValues.functionId || null}
                onChange={(value) => {
                  setFormValues(prev => ({ ...prev, functionId: value || '' }));
                  if (value !== OTHER_FUNCTION_VALUE) {
                    setNewFunctionName('');
                  }
                }}
                options={functionOptions}
                getOptionLabel={(option) => option.id === OTHER_FUNCTION_VALUE ? option.name : `${option.name}${option.department ? ` (${option.department.name})` : ''}`}
                getOptionValue={(option) => option.id}
                disabled={functionsLoading || (formValues.role !== 'VISITOR' && !formValues.departmentId)}
              />
          </Grid>
          )}
          {!defaultRole && formValues.functionId === OTHER_FUNCTION_VALUE && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="newFunctionName"
              name="newFunctionName"
              label="Nom de la nouvelle fonction"
              value={newFunctionName}
              onChange={(e) => setNewFunctionName(e.target.value)}
              margin="normal"
              required
              helperText="Cette fonction sera créée dans le département sélectionné"
            />
          </Grid>
          )}
          <Grid item xs={12} md={6}>
            <PhoneNumberField
              id="phoneNumber"
              name="phoneNumber"
              label="Numéro de téléphone"
              value={formValues.phoneNumber}
              onChange={(value) => {
                setFormValues(prev => ({ ...prev, phoneNumber: value || '' }));
              }}
              onBlur={() => setTouchedFields(prev => ({ ...prev, phoneNumber: true }))}
              error={touchedFields.phoneNumber && Boolean(formErrors.phoneNumber)}
              helperText={touchedFields.phoneNumber && formErrors.phoneNumber}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.otpEnabled}
                  onChange={(e) => {
                    setFormValues({
                      ...formValues,
                      otpEnabled: e.target.checked
                    });
                  }}
                  name="otpEnabled"
                />
              }
              label="Activer l'authentification à deux facteurs"
              sx={{ mt: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : (isEdit ? 'Mettre à jour l\'utilisateur' : 'Créer l\'utilisateur')}
            </Button>
          </Grid>
        </Grid>
      </form>
    </StyledFormBox>
  );
}

export default UserForm; 
